import { Alert, Input, message, Modal, Radio } from 'antd-v5';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getRequest, postRequest } from '../../../../core/apiRequests';
import { useQuery } from 'react-query';
import { useCallback, useMemo, useState } from 'react';
import { setAssociateCount } from '../../../../reducers/associate';

const ReassignAllClientsModal = ({ open, onClose, associateId, associateName, merchantId, stores }) => {
  const [search, setSearch] = useState('');
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const storeIds = useMemo(() => (stores || []).map(s => String(s.store_id)), [stores]);

  const { data } = useQuery(
    ['associates-reassign-all', merchantId],
    async () => {
      const reqObj = {
        params: ['associates', merchantId, 'getAllAssociateList'],
        loadingStatusOff: true,
      };
      return getRequest(reqObj);
    },
    {
      enabled: !!merchantId && open,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const eligibleAssociates = useMemo(() => {
    if (!data || !storeIds.length) return [];
    return data
      .filter(asct => {
        if (asct.id === associateId) return false;
        const asctStoreIds = asct.store_ids || [];
        return asctStoreIds.some(sid => storeIds.includes(String(sid)));
      })
      .sort((a, b) => {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [data, storeIds, associateId]);

  const filteredAssociates = useMemo(() => {
    if (!eligibleAssociates) return [];
    if (search === '') return eligibleAssociates;
    return eligibleAssociates.filter(
      asct =>
        asct?.first_name?.toLowerCase()?.includes(search.toLowerCase()) ||
        asct?.last_name?.toLowerCase()?.includes(search.toLowerCase()),
    );
  }, [eligibleAssociates, search]);

  const handleClose = useCallback(() => {
    setConfirmLoading(false);
    setSelectedAssociate(null);
    setShowError(false);
    setSearch('');
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (selectedAssociate === null) {
      setShowError(true);
      return;
    }

    const targetAssociate = eligibleAssociates.find(a => a.id === selectedAssociate);
    const targetName = targetAssociate ? `${targetAssociate.first_name || ''} ${targetAssociate.last_name || ''}`.trim() : 'the selected associate';

    const confirmed = await new Promise(resolve => {
      Modal.confirm({
        title: 'Confirm Reassignment',
        content: `This will reassign all clients, open reminders, and open sales opportunities from ${associateName} to ${targetName}. This action cannot be undone. Are you sure?`,
        okText: 'Reassign All',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    try {
      setConfirmLoading(true);
      const response = await postRequest({
        params: ['clients', 'reassignAllByAssociate'],
        data: {
          fromAssociateId: associateId,
          toAssociateId: selectedAssociate,
        },
      });
      setAssociateCount(0);
      message.success(`Successfully reassigned ${response?.count ?? 'all'} clients`);
      handleClose();
    } catch (error) {
      console.error(error);
      message.error('Failed to reassign clients');
      setConfirmLoading(false);
    }
  }, [selectedAssociate, associateId, associateName, eligibleAssociates, handleClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      closable={false}
      okText="Reassign All"
      title="Reassign All Clients"
      styles={{
        header: {
          textAlign: 'center',
        },
      }}
      onOk={handleSave}
      confirmLoading={confirmLoading}
    >
      <div className="flex-col-start gap-16" style={{ maxHeight: '60vh', minHeight: '50vh', overflow: 'hidden' }}>
        <div className="flex-row gap-8">
          <ExclamationCircleOutlined />
          <span className="fs-12">
            All clients currently assigned to {associateName} will be reassigned to the chosen associate. This will also
            reassign any open reminders and sales opportunities. This action cannot be undone.
          </span>
        </div>
        {showError && (
          <Alert
            message="Please select an associate"
            type="error"
            showIcon
            closable
            onClose={() => setShowError(false)}
          />
        )}
        <Input.Search onChange={e => setSearch(e.target.value)} value={search} />
        <Radio.Group
          style={{ width: '100%', maxHeight: '100%', overflowX: 'hidden' }}
          className="scrollbar-y"
          value={selectedAssociate}
          onChange={e => setSelectedAssociate(e.target.value)}
        >
          {filteredAssociates?.map(({ id, first_name, last_name }) => (
            <div
              className="p-top-14 p-btm-13 w-full p-left-8 flex-row-spacebetween-nowrap"
              style={{ borderBottom: '1px solid #CAC4D0' }}
              key={id}
            >
              <Radio value={id} className="w-100-P">
                {`${first_name || ''} ${last_name || ''}`}
              </Radio>
            </div>
          ))}
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default ReassignAllClientsModal;
